import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Badge, Button, Form, Spinner, Table,
} from 'react-bootstrap';
import Error from '../Error';
import CounterpartyPicker from '../CounterpartyPicker';
import KnowledgeBaseService, {
  SCOPES, SCOPE_LABELS, scopeNeedsCounterparty,
} from '../../Services/KnowledgeBaseService';
import { formatDateTime } from '../../Utils/timezone';

/**
 * The documents inside one knowledge base.
 *
 * Shared by the admin screen (where the scope filter is the point) and by the
 * Documents modal on a counterparty's row (where the scope is already fixed
 * and the filter would be a control with one setting). `fixedScope` /
 * `fixedCounterpartyId` switch between the two without a second component.
 *
 * Filtering is done BY THE SERVER — `?scope=` and `?counterparty_id=` are on
 * the contract — rather than fetched-all-and-filtered-here, so a knowledge
 * base with a few thousand documents does not have to come down the wire to
 * answer "what has this one operator got".
 */

const SCOPE_VARIANTS = {
  [SCOPES.GENERAL]: 'secondary',
  [SCOPES.OPERATOR]: 'info',
  [SCOPES.PARTNER]: 'primary',
};

/** 1536 → "1.5 KB". Local to this module; nothing else in the app sizes files. */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(n) / Math.log(1024)), units.length - 1);
  const value = n / (1024 ** i);
  return `${i === 0 ? value : value.toFixed(1)} ${units[i]}`;
}

function ScopeCell({ doc }) {
  const scope = doc.scope || SCOPES.GENERAL;
  return (
    <>
      <Badge bg={SCOPE_VARIANTS[scope] || 'secondary'}>
        {SCOPE_LABELS[scope] || scope}
      </Badge>
      {scopeNeedsCounterparty(scope) && (
        <div className="small mt-1">
          {/* The resolved name is what identifies the counterparty; the id is
              the fallback for a record that has since gone away. */}
          {doc.counterparty_name ? (
            <span title={doc.counterparty_id}>{doc.counterparty_name}</span>
          ) : (
            <code className="text-muted" title={doc.counterparty_id}>
              {doc.counterparty_id || 'unknown'}
            </code>
          )}
        </div>
      )}
    </>
  );
}

function KnowledgeBaseDocuments({
  kbId,
  /** Set to hide the scope filter and ask the server for one scope only. */
  fixedScope = null,
  fixedCounterpartyId = null,
  canManage = false,
  /** Bump to force a refetch from outside (e.g. after an upload). */
  reloadToken = 0,
  emptyMessage = 'No documents yet.',
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState('');

  // The admin screen's own filter. Inert when the caller fixed the scope.
  const [scopeFilter, setScopeFilter] = useState('');
  const [counterpartyFilter, setCounterpartyFilter] = useState('');

  const scope = fixedScope || scopeFilter;
  const counterpartyId = fixedCounterpartyId
    || (scopeNeedsCounterparty(scopeFilter) ? counterpartyFilter : '');

  const fetchDocuments = useCallback(async () => {
    if (!kbId) {
      setDocuments([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await KnowledgeBaseService.listDocuments(kbId, { scope, counterpartyId });
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [kbId, scope, counterpartyId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments, reloadToken]);

  const handleDelete = async (doc) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete "${doc.filename}" from this knowledge base?`)) return;
    setDeleting(doc.key);
    setError('');
    try {
      await KnowledgeBaseService.remove(kbId, doc.key);
      await fetchDocuments();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete the document');
    } finally {
      setDeleting('');
    }
  };

  const totalBytes = useMemo(
    () => documents.reduce((sum, d) => sum + (Number(d.size) || 0), 0),
    [documents],
  );

  return (
    <div>
      <Error error={error} setError={setError} />

      {!fixedScope && (
        <div className="row g-2 align-items-end mb-3">
          <div className="col-12 col-md-4">
            <Form.Group controlId="kb-docs-scope">
              <Form.Label className="small text-muted mb-1">Scope</Form.Label>
              <Form.Select
                value={scopeFilter}
                onChange={(e) => {
                  setScopeFilter(e.target.value);
                  setCounterpartyFilter('');
                }}
              >
                <option value="">All scopes</option>
                {Object.values(SCOPES).map((value) => (
                  <option key={value} value={value}>{SCOPE_LABELS[value]}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
          {scopeNeedsCounterparty(scopeFilter) && (
            <div className="col-12 col-md-8">
              <CounterpartyPicker
                kind={scopeFilter}
                value={counterpartyFilter}
                onChange={(id) => setCounterpartyFilter(id)}
                id="kb-docs-counterparty"
                label={null}
                allowAny
                anyLabel="Any"
              />
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="text-muted ms-2">Loading documents…</span>
        </div>
      ) : (
        <>
          <Table
            responsive
            striped
            bordered
            hover
            size="sm"
            className="responsive-card-table"
            aria-label="Documents"
          >
            <thead>
              <tr>
                <th>File</th>
                <th>Scope</th>
                <th>Size</th>
                <th>Uploaded</th>
                {canManage && <th style={{ width: '60px' }} aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {documents.length === 0 ? (
                <tr>
                  <td colSpan={canManage ? 5 : 4} className="text-center text-muted py-4">
                    <i className="fa fa-file-lines fa-2x mb-2 d-block opacity-50" aria-hidden="true" />
                    {emptyMessage}
                  </td>
                </tr>
              ) : documents.map((doc) => (
                <tr key={doc.key}>
                  <td data-label="File">
                    <span className="d-block text-truncate" style={{ maxWidth: '320px' }} title={doc.key}>
                      {doc.filename || doc.key}
                    </span>
                    {doc.content_type && (
                      <small className="text-muted">{doc.content_type}</small>
                    )}
                  </td>
                  <td data-label="Scope"><ScopeCell doc={doc} /></td>
                  <td data-label="Size">{formatBytes(doc.size)}</td>
                  <td data-label="Uploaded">{doc.uploaded_at ? formatDateTime(doc.uploaded_at) : '—'}</td>
                  {canManage && (
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deleting === doc.key}
                        onClick={() => handleDelete(doc)}
                        aria-label={`Delete ${doc.filename || doc.key}`}
                      >
                        {deleting === doc.key
                          ? <i className="fa fa-spinner fa-spin" aria-hidden="true" />
                          : <i className="fa fa-trash" aria-hidden="true" />}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>

          {documents.length > 0 && (
            <div className="text-muted small">
              {`${documents.length} document${documents.length === 1 ? '' : 's'} · ${formatBytes(totalBytes)} total`}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default KnowledgeBaseDocuments;
